import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';

import { devicesApi, api } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { Button } from '../../components/common/Button';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';

// Component to auto-fit map bounds to show all markers
function MapBoundsHandler({ positions, selectedPosition }: {
  positions: [number, number][];
  selectedPosition: [number, number] | null;
}) {
  const map = useMap();

  useEffect(() => {
    if (selectedPosition) {
      map.setView(selectedPosition, 16, { animate: true });
    } else if (positions.length > 0) {
      const bounds = L.latLngBounds(positions.map(([lat, lng]) => L.latLng(lat, lng)));
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 14 });
    }
  }, [positions, selectedPosition, map]);

  return null;
}

type VehicleType = 'MOTORCYCLE' | 'CAR' | 'TRUCK' | 'OTHER';

interface Device {
  id: string;
  imei: string;
  name: string | null;
  deviceType: 'LOAN' | 'SUBSCRIPTION';
  status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';
  vehicleType: VehicleType | null;
  vehiclePlate: string | null;
  vehicleBrand: string | null;
  vehicleModel: string | null;
  vehicleYear: number | null;
  vehicleColor: string | null;
  isOffline: boolean;
  isOverdue: boolean;
  isInRepo: boolean;
  isAggressive: boolean;
  client: {
    id: string;
    name: string;
    phone: string | null;
    email: string | null;
    address: string | null;
  } | null;
  company: {
    id: string;
    name: string;
    code: string;
  };
}

interface Position {
  latitude: number;
  longitude: number;
  speed: number | null;
  heading: number | null;
  deviceTime: string;
  serverTime: string;
}

interface ClientRepoData {
  id: string;
  photos: string[];
  phoneNumbers: string[];
  additionalInfo: Record<string, unknown> | null;
  createdAt: string;
  expiresAt: string;
}

// SVG icons for different vehicle types
const motorcycleSvg = (color: string) => `
  <svg viewBox="0 0 24 24" fill="${color}" xmlns="http://www.w3.org/2000/svg">
    <circle cx="5" cy="17" r="3" stroke="white" stroke-width="1"/>
    <circle cx="19" cy="17" r="3" stroke="white" stroke-width="1"/>
    <path d="M5 17h4l3-6h4l2 6h1" stroke="white" stroke-width="1" fill="none"/>
    <path d="M12 11V8h3l1 3" stroke="white" stroke-width="1" fill="none"/>
    <circle cx="12" cy="7" r="1.5" fill="white"/>
  </svg>
`;

const carSvg = (color: string) => `
  <svg viewBox="0 0 24 24" fill="${color}" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 14l2-7h14l2 7v5H3v-5z" stroke="white" stroke-width="1"/>
    <circle cx="7" cy="17" r="2" fill="white"/>
    <circle cx="17" cy="17" r="2" fill="white"/>
    <path d="M5 10h14" stroke="white" stroke-width="0.5"/>
    <rect x="6" y="8" width="5" height="3" fill="white" opacity="0.5"/>
    <rect x="13" y="8" width="5" height="3" fill="white" opacity="0.5"/>
  </svg>
`;

const truckSvg = (color: string) => `
  <svg viewBox="0 0 24 24" fill="${color}" xmlns="http://www.w3.org/2000/svg">
    <rect x="1" y="8" width="14" height="10" stroke="white" stroke-width="1"/>
    <path d="M15 12h5l2 4v2h-7v-6z" stroke="white" stroke-width="1"/>
    <circle cx="6" cy="18" r="2" fill="white"/>
    <circle cx="18" cy="18" r="2" fill="white"/>
  </svg>
`;

const defaultSvg = (color: string) => `
  <svg viewBox="0 0 24 24" fill="${color}" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="8" stroke="white" stroke-width="2"/>
    <circle cx="12" cy="12" r="3" fill="white"/>
  </svg>
`;

// Create marker icon based on vehicle type and aggressive status
const createVehicleIcon = (vehicleType: VehicleType | null, isAggressive: boolean) => {
  const color = isAggressive ? '#dc2626' : '#9333ea'; // red for aggressive, purple for normal
  let svgContent: string;

  switch (vehicleType) {
    case 'MOTORCYCLE':
      svgContent = motorcycleSvg(color);
      break;
    case 'CAR':
      svgContent = carSvg(color);
      break;
    case 'TRUCK':
      svgContent = truckSvg(color);
      break;
    default:
      svgContent = defaultSvg(color);
  }

  return L.divIcon({
    className: 'custom-vehicle-marker',
    html: `<div style="width: 36px; height: 36px; filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));">${svgContent}</div>`,
    iconSize: [36, 36],
    iconAnchor: [18, 18],
    popupAnchor: [0, -18],
  });
};

export function RepoAgentPage() {
  const user = useAuthStore((state) => state.user);
  const queryClient = useQueryClient();
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [showRepoDataModal, setShowRepoDataModal] = useState(false);
  const [showRecoverConfirm, setShowRecoverConfirm] = useState(false);

  // Fetch assigned devices for repo agent
  const { data: devicesData, isLoading: isDevicesLoading } = useQuery({
    queryKey: ['repo-agent-devices', user?.id],
    queryFn: async () => {
      // Get devices assigned to this repo agent
      const response = await devicesApi.getAssigned();
      return response.data;
    },
  });

  // Fetch last positions for all assigned devices
  const { data: positionsData } = useQuery({
    queryKey: ['repo-agent-positions', devicesData?.data?.map((d: Device) => d.id)],
    queryFn: async () => {
      const devices = devicesData?.data || [];
      const positions: Record<string, Position> = {};

      await Promise.all(
        devices.map(async (device: Device) => {
          try {
            const response = await devicesApi.getLastPosition(device.id);
            if (response.data?.data) {
              positions[device.id] = response.data.data;
            }
          } catch (error) {
            // Device has no position data
          }
        })
      );

      return positions;
    },
    enabled: !!devicesData?.data?.length,
  });

  // Fetch client repo data for selected device
  const { data: repoData, isLoading: isRepoDataLoading } = useQuery({
    queryKey: ['client-repo-data', selectedDevice?.client?.id],
    queryFn: async () => {
      if (!selectedDevice?.client?.id) return null;
      const response = await api.get(`/clients/${selectedDevice.client.id}/repo-data`);
      return response.data;
    },
    enabled: !!selectedDevice?.client?.id && showRepoDataModal,
  });

  // Mark device repo complete
  const markRepoCompleteMutation = useMutation({
    mutationFn: async (deviceId: string) => {
      return devicesApi.markNotRepo(deviceId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['repo-agent-devices'] });
      setSelectedDevice(null);
    },
  });

  const devices = (devicesData?.data || []) as Device[];
  const positions = positionsData || {};
  const clientRepoData = repoData?.data as ClientRepoData | null;

  // Get all positions for map bounds
  const allPositions = useMemo<[number, number][]>(() => {
    return devices
      .filter((d) => positions[d.id])
      .map((d) => [positions[d.id].latitude, positions[d.id].longitude]);
  }, [devices, positions]);

  // Get selected device position
  const selectedPosition = useMemo<[number, number] | null>(() => {
    if (selectedDevice && positions[selectedDevice.id]) {
      return [positions[selectedDevice.id].latitude, positions[selectedDevice.id].longitude];
    }
    return null;
  }, [selectedDevice, positions]);

  // Default map center
  const mapCenter: [number, number] = [18.4861, -69.9312]; // Santo Domingo

  if (isDevicesLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 bg-white border-b">
        <h1 className="text-xl font-bold text-gray-900">Panel de Incaute</h1>
        <p className="text-sm text-gray-600">
          {devices.length} dispositivos asignados
        </p>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Device List */}
        <div className="w-96 border-r bg-white overflow-y-auto">
          {devices.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              No tienes dispositivos asignados para incaute
            </div>
          ) : (
            <div className="divide-y">
              {devices.map((device) => (
                <div
                  key={device.id}
                  className={`p-4 cursor-pointer hover:bg-gray-50 ${
                    selectedDevice?.id === device.id ? 'bg-blue-50' : ''
                  } ${device.isAggressive ? 'border-l-4 border-red-500' : ''}`}
                  onClick={() => setSelectedDevice(device)}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="font-medium text-gray-900">
                        {device.name || device.imei}
                      </div>
                      <div className="text-xs text-gray-500">{device.imei}</div>
                    </div>
                    <div className="flex gap-1">
                      {device.isAggressive && (
                        <span className="px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded">
                          Agresivo
                        </span>
                      )}
                      {device.isOffline && (
                        <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded">
                          Offline
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Vehicle Info */}
                  {device.vehiclePlate && (
                    <div className="mb-2 text-sm">
                      <span className="font-mono bg-gray-100 px-2 py-0.5 rounded">
                        {device.vehiclePlate}
                      </span>
                      {device.vehicleBrand && (
                        <span className="ml-2 text-gray-600">
                          {device.vehicleBrand} {device.vehicleModel} {device.vehicleYear}
                        </span>
                      )}
                      {device.vehicleColor && (
                        <span className="ml-1 text-gray-500">({device.vehicleColor})</span>
                      )}
                    </div>
                  )}

                  {/* Client Info */}
                  {device.client && (
                    <div className="mt-2 p-2 bg-gray-50 rounded text-sm">
                      <div className="font-medium">{device.client.name}</div>
                      {device.client.phone && (
                        <div className="text-gray-600">{device.client.phone}</div>
                      )}
                      {device.client.address && (
                        <div className="text-gray-500 text-xs">{device.client.address}</div>
                      )}
                    </div>
                  )}

                  {/* Company */}
                  <div className="mt-2 text-xs text-gray-500">
                    {device.company.name} ({device.company.code})
                  </div>

                  {/* Position Info */}
                  {positions[device.id] && (
                    <div className="mt-2 text-xs text-gray-400">
                      Última posicion:{' '}
                      {format(new Date(positions[device.id].deviceTime), 'dd/MM/yyyy HH:mm')}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Map and Details */}
        <div className="flex-1 flex flex-col">
          {/* Map */}
          <div className="flex-1 relative">
            <MapContainer
              center={mapCenter}
              zoom={10}
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />

              {/* Auto-fit bounds to show all devices */}
              <MapBoundsHandler positions={allPositions} selectedPosition={selectedPosition} />

              {devices.map((device) => {
                const pos = positions[device.id];
                if (!pos) return null;

                return (
                  <Marker
                    key={device.id}
                    position={[pos.latitude, pos.longitude]}
                    icon={createVehicleIcon(device.vehicleType, device.isAggressive)}
                    eventHandlers={{
                      click: () => setSelectedDevice(device),
                    }}
                  >
                    <Popup>
                      <div className="min-w-48">
                        <div className="font-bold">{device.name || device.imei}</div>
                        {device.vehiclePlate && (
                          <div className="font-mono text-sm">{device.vehiclePlate}</div>
                        )}
                        {device.vehicleType && (
                          <div className="text-xs text-gray-500">
                            {device.vehicleType === 'MOTORCYCLE' ? 'Motocicleta' :
                             device.vehicleType === 'CAR' ? 'Vehículo' :
                             device.vehicleType === 'TRUCK' ? 'Camión' : 'Otro'}
                          </div>
                        )}
                        {device.client && (
                          <div className="mt-1 text-sm text-gray-600">
                            {device.client.name}
                          </div>
                        )}
                        <div className="mt-1 text-xs text-gray-500">
                          {format(new Date(pos.deviceTime), 'dd/MM/yyyy HH:mm')}
                        </div>
                        {pos.speed !== null && (
                          <div className="text-xs text-gray-500">
                            Velocidad: {Math.round(pos.speed)} km/h
                          </div>
                        )}
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
            </MapContainer>
          </div>

          {/* Selected Device Actions */}
          {selectedDevice && (
            <div className="p-4 bg-white border-t">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="font-medium">{selectedDevice.name || selectedDevice.imei}</h3>
                  {selectedDevice.vehiclePlate && (
                    <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                      {selectedDevice.vehiclePlate}
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  <Link to={`/devices/${selectedDevice.id}`}>
                    <Button variant="secondary" size="sm">
                      Ver detalle
                    </Button>
                  </Link>
                  <Link to={`/history?device=${selectedDevice.id}`}>
                    <Button variant="secondary" size="sm">
                      Ver historial
                    </Button>
                  </Link>
                  {selectedDevice.client && (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => setShowRepoDataModal(true)}
                    >
                      Datos de incaute
                    </Button>
                  )}
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => setShowRecoverConfirm(true)}
                    isLoading={markRepoCompleteMutation.isPending}
                  >
                    Marcar recuperado
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        isOpen={showRecoverConfirm}
        title="Marcar como recuperado"
        message="Marcar este dispositivo como recuperado? Esta accion completa el proceso de incaute."
        confirmLabel="Marcar recuperado"
        destructive
        isLoading={markRepoCompleteMutation.isPending}
        onConfirm={() => {
          if (selectedDevice) {
            markRepoCompleteMutation.mutate(selectedDevice.id);
          }
          setShowRecoverConfirm(false);
        }}
        onClose={() => setShowRecoverConfirm(false)}
      />

      {/* Repo Data Modal */}
      {showRepoDataModal && selectedDevice?.client && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b flex justify-between items-center">
              <div>
                <h2 className="text-lg font-bold">Datos de Incaute</h2>
                <p className="text-sm text-gray-500">{selectedDevice.client.name}</p>
              </div>
              <button
                onClick={() => setShowRepoDataModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-4 overflow-y-auto max-h-[calc(90vh-120px)]">
              {isRepoDataLoading ? (
                <div className="flex justify-center py-8">
                  <LoadingSpinner />
                </div>
              ) : clientRepoData ? (
                <div className="space-y-6">
                  {/* Client Basic Info */}
                  <div>
                    <h3 className="font-medium text-gray-900 mb-2">Información del Cliente</h3>
                    <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                      <div>
                        <span className="text-sm text-gray-500">Nombre:</span>
                        <span className="ml-2 font-medium">{selectedDevice.client.name}</span>
                      </div>
                      {selectedDevice.client.phone && (
                        <div>
                          <span className="text-sm text-gray-500">Teléfono:</span>
                          <a
                            href={`tel:${selectedDevice.client.phone}`}
                            className="ml-2 font-medium text-blue-600 hover:underline"
                          >
                            {selectedDevice.client.phone}
                          </a>
                        </div>
                      )}
                      {selectedDevice.client.email && (
                        <div>
                          <span className="text-sm text-gray-500">Email:</span>
                          <span className="ml-2">{selectedDevice.client.email}</span>
                        </div>
                      )}
                      {selectedDevice.client.address && (
                        <div>
                          <span className="text-sm text-gray-500">Dirección:</span>
                          <span className="ml-2">{selectedDevice.client.address}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Additional Phone Numbers */}
                  {clientRepoData.phoneNumbers && clientRepoData.phoneNumbers.length > 0 && (
                    <div>
                      <h3 className="font-medium text-gray-900 mb-2">Teléfonos Adicionales</h3>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <div className="flex flex-wrap gap-2">
                          {clientRepoData.phoneNumbers.map((phone, idx) => (
                            <a
                              key={idx}
                              href={`tel:${phone}`}
                              className="px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                            >
                              {phone}
                            </a>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Photos */}
                  {clientRepoData.photos && clientRepoData.photos.length > 0 && (
                    <div>
                      <h3 className="font-medium text-gray-900 mb-2">Fotos</h3>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {clientRepoData.photos.map((photo, idx) => (
                          <a
                            key={idx}
                            href={photo}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block aspect-square rounded overflow-hidden hover:opacity-80"
                          >
                            <img
                              src={photo}
                              alt={`Foto ${idx + 1}`}
                              className="w-full h-full object-cover"
                            />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Additional Info */}
                  {clientRepoData.additionalInfo && (
                    <div>
                      <h3 className="font-medium text-gray-900 mb-2">Información Adicional</h3>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <pre className="text-sm whitespace-pre-wrap">
                          {JSON.stringify(clientRepoData.additionalInfo, null, 2)}
                        </pre>
                      </div>
                    </div>
                  )}

                  {/* Vehicle Info */}
                  {(selectedDevice.vehiclePlate || selectedDevice.vehicleBrand) && (
                    <div>
                      <h3 className="font-medium text-gray-900 mb-2">Información del Vehículo</h3>
                      <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                        {selectedDevice.vehiclePlate && (
                          <div>
                            <span className="text-sm text-gray-500">Placa:</span>
                            <span className="ml-2 font-mono font-bold">
                              {selectedDevice.vehiclePlate}
                            </span>
                          </div>
                        )}
                        {selectedDevice.vehicleBrand && (
                          <div>
                            <span className="text-sm text-gray-500">Vehículo:</span>
                            <span className="ml-2">
                              {selectedDevice.vehicleBrand} {selectedDevice.vehicleModel}{' '}
                              {selectedDevice.vehicleYear}
                            </span>
                          </div>
                        )}
                        {selectedDevice.vehicleColor && (
                          <div>
                            <span className="text-sm text-gray-500">Color:</span>
                            <span className="ml-2">{selectedDevice.vehicleColor}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Expiration Warning */}
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <span className="text-yellow-600 mr-2">!</span>
                      <div>
                        <div className="font-medium text-yellow-800">
                          Información sensible
                        </div>
                        <div className="text-sm text-yellow-700">
                          Estos datos expiran el{' '}
                          {format(new Date(clientRepoData.expiresAt), 'dd/MM/yyyy')} y seran
                          eliminados automaticamente.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No hay datos de incaute disponibles para este cliente
                </div>
              )}
            </div>

            <div className="p-4 border-t flex justify-end">
              <Button variant="secondary" onClick={() => setShowRepoDataModal(false)}>
                Cerrar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
